import { projectToSvg, type VectorExportSettings } from "./sceneToSvg";
import type { Project } from "../model/Project";
import type { StampLibrary } from "../stamps/StampLibrary";
import { triggerDownload } from "./download";
import { getUnits } from "../preferences";

/**
 * Modern SVG for full vector editors (Affinity / Illustrator / Inkscape).
 * Geometry stays in native millimetres — these tools honour the document's
 * `width`/`height` units and viewBox, so no coordinate remapping is needed and
 * curves are preserved at full fidelity.
 */
export function exportSvg(project: Project, library: StampLibrary): Blob {
  const { svg } = projectToSvg(project, library, { unit: "mm" });
  return new Blob([svg], { type: "image/svg+xml" });
}

/**
 * Cricut-compatible SVG. Cricut Design Space ignores the document unit and
 * reads raw coordinates, so we remap geometry into the user's active unit; it
 * also rejects clip paths / pattern fills and parses cleanest from plain
 * polylines, so the scene is flattened and tiled fills are dropped. See #444.
 */
export function exportSvgCricut(project: Project, library: StampLibrary): Blob {
  const { svg } = projectToSvg(project, library, {
    unit: getUnits(),
    cricut: true,
  });
  return new Blob([svg], { type: "image/svg+xml" });
}

/**
 * Optimized vector export (#572): the settings dialog's SVG — content
 * selection, per-operation laser colors / hairlines, calibration boxes, and
 * optionally the Cricut compatibility pass (unit remap + flatten + strip).
 */
export function exportSvgOptimized(
  project: Project,
  library: StampLibrary,
  settings: VectorExportSettings,
  cricut: boolean,
): Blob {
  const { svg } = projectToSvg(project, library, {
    vector: settings,
    ...(cricut ? { unit: getUnits(), cricut: true } : { unit: "mm" as const }),
  });
  return new Blob([svg], { type: "image/svg+xml" });
}

export function downloadSvg(project: Project, library: StampLibrary) {
  const blob = exportSvg(project, library);
  triggerDownload(blob, `${sanitize(project.name)}.svg`);
}

export function downloadSvgOptimized(
  project: Project,
  library: StampLibrary,
  settings: VectorExportSettings,
  cricut: boolean,
) {
  const blob = exportSvgOptimized(project, library, settings, cricut);
  triggerDownload(
    blob,
    `${sanitize(project.name)}${cricut ? "-cricut" : ""}.svg`,
  );
}

export function downloadSvgCricut(project: Project, library: StampLibrary) {
  const blob = exportSvgCricut(project, library);
  triggerDownload(blob, `${sanitize(project.name)}-cricut.svg`);
}

function sanitize(s: string): string {
  return s.replace(/[^a-z0-9_-]/gi, "_") || "leather-art";
}
